import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/**
 * Handles the Emergent Auth redirect landing.
 * The URL fragment carries `session_id`. We exchange it via backend once,
 * then navigate to the home page.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { setUserDirect } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;

    // Clear the fragment from URL immediately (once)
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    if (!session_id) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        setUserDirect(data);
        toast.success(`Bienvenue ${data.name}`);
        nav("/portefeuille", { replace: true });
      } catch (e) {
        // Detailed logging so the user can share console output if flow keeps failing
        console.error("[AuthCallback] Google session exchange failed", {
          status: e?.response?.status,
          detail: e?.response?.data,
          message: e?.message,
        });
        const detail = e?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? `Connexion Google échouée : ${detail}`
            : "Connexion Google échouée. Vérifiez que votre navigateur autorise les cookies tiers (Safari/Brave peuvent les bloquer)."
        );
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, nav, setUserDirect]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-12">
      <div className="flex flex-col items-center gap-3 text-zinc-400">
        <span className="w-12 h-12 rounded-md bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
          <TrendingUp className="w-6 h-6 text-emerald-400" />
        </span>
        <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
        <div className="text-sm">Connexion en cours…</div>
      </div>
    </div>
  );
}
